import { useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { BadgeCheck, ChevronLeft, ChevronRight, Copy, ExternalLink, Send, UserRoundPlus, X } from "lucide-react";

import "./AdminMasterLeadsPage.css";

import { AdminMasterLeadFilter, IAdminMasterLead, MasterLeadStatuses } from "./AdminMasterLeadsService";
import { AdminMasterLeadsStore } from "./AdminMasterLeadsStore";

interface StatusFilter {
  title: string;
  value: AdminMasterLeadFilter;
}

const filters: StatusFilter[] = [
  {
    title: "Новые",
    value: MasterLeadStatuses.Created,
  },
  {
    title: "Связались",
    value: MasterLeadStatuses.Contacted,
  },
  {
    title: "Конвертированы",
    value: MasterLeadStatuses.Converted,
  },
  {
    title: "Отклонены",
    value: MasterLeadStatuses.Cancelled,
  },
  {
    title: "Все",
    value: "",
  },
];

const statusText: Record<string, string> = {
  created: "Новая",
  new: "Новая",

  contacted: "Связались",

  converted: "Конвертирована",

  cancelled: "Отклонена",
  rejected: "Отклонена",
};

const sourceText: Record<string, string> = {
  master_landing: "Лендинг мастеров",
  landing: "Лендинг",
  admin: "Админка",
  direct: "Прямая заявка",
};

const formatDate = (value: string): string => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getContactHref = (contact: string): string | null => {
  const value = contact.trim();

  if (!value) return null;

  if (value.startsWith("http://") || value.startsWith("https://")) {
    return value;
  }

  if (value.startsWith("t.me/") || value.startsWith("telegram.me/")) {
    return `https://${value}`;
  }

  if (value.startsWith("@")) {
    return `https://t.me/${value.slice(1)}`;
  }

  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
    return `mailto:${value}`;
  }

  const phone = value.replace(/[^\d+]/g, "");
  const digits = phone.replace(/\D/g, "");

  if (digits.length >= 7) {
    return `tel:${phone}`;
  }

  return null;
};

const getSourceTitle = (source: string): string => {
  const normalized = source?.trim();

  if (!normalized) return "Не указан";

  return sourceText[normalized] ?? normalized;
};

export const AdminMasterLeadsPage = observer(() => {
  const store = useMemo(() => new AdminMasterLeadsStore(), []);

  const [toastMessage, setToastMessage] = useState("");

  const toastTimerRef = useRef<number | null>(null);

  useEffect(() => {
    void store.load();

    return () => {
      if (toastTimerRef.current !== null) {
        window.clearTimeout(toastTimerRef.current);
      }
    };
  }, [store]);

  const showToast = (message: string) => {
    setToastMessage(message);

    if (toastTimerRef.current !== null) {
      window.clearTimeout(toastTimerRef.current);
    }

    toastTimerRef.current = window.setTimeout(() => {
      setToastMessage("");
      toastTimerRef.current = null;
    }, 2200);
  };

  const copyText = async (value: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(value);

      showToast(successMessage);
    } catch {
      showToast("Не удалось скопировать");
    }
  };

  const copyLead = async (item: IAdminMasterLead) => {
    const text = [
      `Имя: ${item.name || "—"}`,
      `Контакт: ${item.contact || "—"}`,
      `Источник: ${getSourceTitle(item.source)}`,
      `Статус: ${statusText[item.status] ?? item.status}`,
      `Дата заявки: ${formatDate(item.createdAt)}`,
    ].join("\n");

    await copyText(text, "Данные заявки скопированы");
  };

  const changeStatus = async (item: IAdminMasterLead, status: typeof MasterLeadStatuses.Contacted | typeof MasterLeadStatuses.Converted | typeof MasterLeadStatuses.Cancelled) => {
    const success = await store.changeStatus(item.id, status);

    if (success) {
      showToast("Статус заявки обновлён");
    }
  };

  return (
    <>
      {toastMessage && (
        <div className="admin-master-leads-toast" role="status">
          {toastMessage}
        </div>
      )}

      <div className="admin-page-head">
        <div>
          <h1 className="admin-page-title">Заявки мастеров</h1>

          <p>
            Всего: <strong>{store.Total}</strong>
          </p>
        </div>

        <div className="admin-master-leads-head-icon">
          <UserRoundPlus size={24} />
        </div>
      </div>

      <div className="admin-master-leads-filters">
        {filters.map((filter) => (
          <button type="button" key={filter.title} className={store.Status === filter.value ? "active" : ""} disabled={store.IsLoading} onClick={() => void store.setStatus(filter.value)}>
            {filter.title}
          </button>
        ))}
      </div>

      {store.IsLoading && !store.Items.length && <section className="admin-card admin-master-leads-message">Загружаем заявки мастеров...</section>}

      {store.Error && (
        <section className="admin-card admin-error admin-master-leads-message">
          <p>{store.Error}</p>

          <button type="button" onClick={() => void store.load()}>
            Повторить
          </button>
        </section>
      )}

      {store.ActionError && <div className="admin-master-leads-action-error">{store.ActionError}</div>}

      {!store.Error && (!store.IsLoading || store.Items.length > 0) && (
        <section className="admin-card admin-master-leads-card">
          <div className="admin-master-leads-table">
            <div className="admin-master-leads-row admin-master-leads-row-head">
              <span>Мастер</span>
              <span>Контакт</span>
              <span>Источник</span>
              <span>Статус</span>
              <span>Создана</span>
              <span>Обновлена</span>
              <span>Действия</span>
            </div>

            {store.Items.map((item) => {
              const contactHref = getContactHref(item.contact);

              const isChanging = store.ChangingId === item.id;

              const isCreated = item.status === MasterLeadStatuses.Created || item.status === "new";

              const isContacted = item.status === MasterLeadStatuses.Contacted;

              return (
                <article className="admin-master-leads-row" key={item.id}>
                  <div className="admin-master-lead-person">
                    <span className="admin-master-lead-avatar">{item.name?.trim().charAt(0).toUpperCase() || "М"}</span>

                    <div>
                      <strong title={item.name || "Мастер"}>{item.name || "Мастер"}</strong>

                      <small>#{item.id}</small>
                    </div>
                  </div>

                  <div className="admin-master-lead-cell" data-label="Контакт">
                    <div className="admin-master-lead-contact">
                      <span title={item.contact || "—"}>{item.contact || "—"}</span>

                      {!!item.contact && (
                        <button type="button" onClick={() => void copyText(item.contact, "Контакт скопирован")} aria-label="Скопировать контакт">
                          <Copy size={14} />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="admin-master-lead-cell" data-label="Источник">
                    <span className="admin-master-lead-source" title={getSourceTitle(item.source)}>
                      {getSourceTitle(item.source)}
                    </span>
                  </div>

                  <div className="admin-master-lead-cell" data-label="Статус">
                    <span className={["admin-master-lead-status", item.status].join(" ")}>{statusText[item.status] ?? item.status}</span>
                  </div>

                  <div className="admin-master-lead-cell" data-label="Создана">
                    <span>{formatDate(item.createdAt)}</span>
                  </div>

                  <div className="admin-master-lead-cell" data-label="Обновлена">
                    <span>{formatDate(item.updatedAt)}</span>
                  </div>

                  <div className="admin-master-lead-cell admin-master-lead-actions-cell" data-label="Действия">
                    <div className="admin-master-lead-actions">
                      <button type="button" className="copy" onClick={() => void copyLead(item)} aria-label="Скопировать заявку">
                        <Copy size={15} />
                        <span>Копировать</span>
                      </button>

                      {contactHref && (
                        <a className="open" href={contactHref} target={contactHref.startsWith("http") ? "_blank" : undefined} rel={contactHref.startsWith("http") ? "noreferrer" : undefined}>
                          <ExternalLink size={15} />
                          <span>Открыть</span>
                        </a>
                      )}

                      {isCreated && (
                        <button type="button" className="contacted" disabled={isChanging} onClick={() => void changeStatus(item, MasterLeadStatuses.Contacted)}>
                          <Send size={15} />

                          <span>{isChanging ? "..." : "Связались"}</span>
                        </button>
                      )}

                      {isContacted && (
                        <button type="button" className="converted" disabled={isChanging} onClick={() => void changeStatus(item, MasterLeadStatuses.Converted)}>
                          <BadgeCheck size={15} />

                          <span>{isChanging ? "..." : "В мастера"}</span>
                        </button>
                      )}

                      {(isCreated || isContacted) && (
                        <button type="button" className="cancelled" disabled={isChanging} onClick={() => void changeStatus(item, MasterLeadStatuses.Cancelled)}>
                          <X size={15} />
                          <span>Отклонить</span>
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          {!store.Items.length && !store.IsLoading && <div className="admin-empty admin-master-leads-empty">Заявок с таким статусом пока нет</div>}

          <div className="admin-master-leads-pagination">
            <button type="button" disabled={!store.CanGoPrev} onClick={() => void store.goPrevPage()}>
              <ChevronLeft size={18} />
              <span>Назад</span>
            </button>

            <span>
              {store.Page} / {store.TotalPages}
            </span>

            <button type="button" disabled={!store.CanGoNext} onClick={() => void store.goNextPage()}>
              <span>Вперёд</span>
              <ChevronRight size={18} />
            </button>
          </div>
        </section>
      )}
    </>
  );
});
